/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useContext, useState } from "react";
import { Box } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { MdOutlineContentCopy } from "react-icons/md";
import { BsFillSendCheckFill } from "react-icons/bs";
import SweetAlert2 from "react-sweetalert2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ProfileContext } from "../../../../store/ProfileProvider";
import ReceiverAccountDetailsModal from "../../shared/ReceiverAccountDetailsModal";
import ResendAccountConfirmModal from "../../receiver/components/ResendAccountConfirmModal";

dayjs.extend(utc);
dayjs.extend(timezone);

type ReceiverAccountRow = {
  receiver_id: string;
  account_id: string;
  asset: string;
  method_name: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  email?: string;
  confirm_status?: string;
  created_at?: string;
  method_details?: Record<string, any>;
  [key: string]: any;
};

interface Props {
  isLoading: boolean;
  tableData: ReceiverAccountRow[] | null;
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: (model: { page: number; pageSize: number }) => void;
  currentLastKey: string;
}

const statusClasses = "status";

interface CustomerStatusItems {
  status: string;
  value: string;
  display: ReactElement;
}

const statusList: CustomerStatusItems[] = [
  {
    status: "unconfirmed",
    value: "unconfirmed",
    display: (
      <Badge className={`${statusClasses} status-unconfirmed`}>
        Unconfirmed
      </Badge>
    ),
  },
  {
    status: "sent_mail",
    value: "sent_mail",
    display: (
      <Badge className={`${statusClasses} status-sent_mail`}>
        Email Sent
      </Badge>
    ),
  },
  {
    status: "confirmed",
    value: "confirmed",
    display: (
      <Badge className={`${statusClasses} status-confirmed`}>Confirmed</Badge>
    ),
  },
];

const capitalizeLabel = (label: string) => {
  let modifiedLabel = label
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  switch (label) {
    case "bsb":
      modifiedLabel = "BSB";
      break;
    case "bic":
      modifiedLabel = "BIC";
      break;
    case "iban":
      modifiedLabel = "IBAN";
      break;
  }

  return modifiedLabel;
};

const ReceiverAccountSearchList: React.FC<Props> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<ReceiverAccountRow | null>(
    null
  );
  const [isOpenResendAccountConfirmModal, setIsOpenResendAccountConfirmModal] =
    useState<boolean>(false);
  const [
    receiverAccountDetailForResendConfirm,
    setReceiverAccountDetailForResendConfirm,
  ] = useState<any>(null);
  const [swalProps, setSwalProps] = useState<any>({});

  const handleCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy id: ", err);
    }
  };

  const formatDate = (timestamp?: string) => {
    if (!timestamp) return "-";
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const formatName = (row: ReceiverAccountRow) => {
    const parts = [row.first_name, row.middle_name, row.last_name].filter(
      (p) => p && String(p).trim().length > 0
    );
    return parts.length > 0 ? parts.join(" ") : "-";
  };

  const formatMethodDetails = (details?: Record<string, any>) => {
    if (!details || typeof details !== "object") return "-";
    const parts = Object.entries(details)
      .filter(([, v]) => v !== "" && v !== null && v !== undefined)
      .map(([k, v]) => `${capitalizeLabel(k)}: ${v}`);
    return parts.length > 0 ? parts.join(" • ") : "-";
  };

  const renderEllipsisCell = (text: string) => (
    <Tooltip title={text} arrow placement="top">
      <div className="block w-full overflow-hidden text-ellipsis whitespace-nowrap">
        {text}
      </div>
    </Tooltip>
  );

  const handleClickDetails = (row: ReceiverAccountRow) => {
    setSelectedRow(row);
    setIsDetailsOpen(true);
  };

  const closeDetailsModal = () => {
    setSelectedRow(null);
    setIsDetailsOpen(false);
  };

  const handleResendAccount = (row: ReceiverAccountRow) => {
    setReceiverAccountDetailForResendConfirm({ ...row, id: row.account_id });
    setIsOpenResendAccountConfirmModal(true);
  };

  const closeResendAccountConfirmModal = () => {
    setReceiverAccountDetailForResendConfirm(null);
    setIsOpenResendAccountConfirmModal(false);
  };

  const columns: GridColDef[] = [
    {
      field: "account_id",
      headerName: "",
      flex: 1,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<ReceiverAccountRow>) => (
        <Tooltip title="Copy Receiver Account ID" arrow placement="top">
          <div>
            <IconButton
              id={"button-copy-account-id-" + params?.row?.account_id}
              onClick={() => handleCopy(params.row.account_id)}
            >
              <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
            </IconButton>
          </div>
        </Tooltip>
      ),
    },
    {
      field: "created_at",
      headerName: "Created Date",
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ReceiverAccountRow>) =>
        renderEllipsisCell(formatDate(params.row.created_at)),
    },
    {
      field: "name",
      headerName: "Receiver Name",
      flex: 1.2,
      minWidth: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ReceiverAccountRow>) =>
        renderEllipsisCell(formatName(params.row)),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ReceiverAccountRow>) =>
        renderEllipsisCell(params.row.email || "-"),
    },
    {
      field: "asset",
      headerName: "Asset",
      minWidth: 90,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ReceiverAccountRow>) =>
        renderEllipsisCell(params.row.asset || "-"),
    },
    {
      field: "method_name",
      headerName: "Method",
      flex: 1,
      minWidth: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ReceiverAccountRow>) =>
        renderEllipsisCell(params.row.method_name || "-"),
    },
    {
      field: "method_details",
      headerName: "Method Details",
      flex: 3,
      minWidth: 320,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ReceiverAccountRow>) =>
        renderEllipsisCell(formatMethodDetails(params.row.method_details)),
    },
    {
      field: "confirm_status",
      headerName: "Confirm Status",
      flex: 1,
      minWidth: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<ReceiverAccountRow>) => {
        const customerStatus = params.row.confirm_status as string;
        const statusItem = statusList.find(
          (item) => item.status === customerStatus
        );
        return statusItem ? statusItem.display : null;
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      flex: 1,
      minWidth: 110,
      renderCell: (params: GridRenderCellParams<ReceiverAccountRow>) => (
        <>
          {params?.row?.confirm_status === "sent_mail" &&
            userRole === "superadmin" && (
              <Tooltip title="Resend Confirmation Link" arrow placement="top">
                <IconButton
                  id={
                    "button-resend-account-confirm-" + params?.row?.account_id
                  }
                  onClick={() => handleResendAccount(params.row)}
                >
                  <BsFillSendCheckFill className="h-5 w-5 text-navy-700 opacity-90" />
                </IconButton>
              </Tooltip>
            )}
          <Tooltip title="Details" arrow placement="top">
            <IconButton
              id={"button-details-" + params?.row?.account_id}
              onClick={() => handleClickDetails(params.row)}
            >
              <AiOutlineInfoCircle className="h-5 w-5 text-navy-700" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start",
    row: "",
  };

  return (
    <div>
      <Box className="card mt-3 w-full">
        <DataGrid
          sx={{
            "& .MuiDataGrid-cell": {
              padding: "8px 16px",
            },
            "& .MuiDataGrid-columnHeader": {
              padding: "8px 16px",
            },
            "& .MuiTablePagination-input": {
              marginRight: "-20px",
            },
            "& .MuiTablePagination-displayedRows": {
              display: "none",
            },
          }}
          loading={isLoading}
          rows={tableData || []}
          columns={columns}
          getRowId={(row) => row.account_id}
          rowCount={rowCount}
          sortingMode="server"
          paginationMode="server"
          pageSizeOptions={[10, 25, 50, 100]}
          rowHeight={40}
          columnHeaderHeight={40}
          paginationModel={{ page: page, pageSize: pageSize }}
          onPaginationModelChange={(newValue) => setPaginationModel(newValue)}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          classes={customDataGridStyles}
          hideFooter={currentLastKey === "" && page === 0}
          autoHeight
          localeText={{
            noRowsLabel: "No receiver accounts found.",
          }}
        />
      </Box>

      {isDetailsOpen && selectedRow && (
        <ReceiverAccountDetailsModal
          isOpen={isDetailsOpen}
          onClose={closeDetailsModal}
          detail={selectedRow}
          receiverId={selectedRow.receiver_id}
        />
      )}

      {isOpenResendAccountConfirmModal &&
        receiverAccountDetailForResendConfirm && (
          <ResendAccountConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={closeResendAccountConfirmModal}
            receiverAccountToResendConfirm={
              receiverAccountDetailForResendConfirm
            }
            isOpen={isOpenResendAccountConfirmModal}
            onClose={closeResendAccountConfirmModal}
          />
        )}

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </div>
  );
};

export default ReceiverAccountSearchList;
